import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Badge, Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CanalDeIntegracao } from '@/data/tipos';

/** Ícone de cada tipo de canal, para a lista não virar uma pilha de texto. */
const ICONE: Record<string, string> = {
    whatsapp: 'message-circle',
    instagram: 'instagram',
    email: 'mail',
    sms: 'smartphone',
    webchat: 'globe',
};

export interface EnviarMensagemProps {
    /** A PESSOA, não a negociação: conversa se prende a quem fala. */
    contatoId: string;
    nome: string;
    tamanho?: 'sm' | 'md';
    variante?: 'primary' | 'secondary' | 'ghost';
}

/**
 * "Enviar mensagem" — do cadastro para a conversa, sem passar pela caixa.
 *
 * O caminho antigo era procurar a pessoa na Comunicação, e ele falha justamente
 * quando importa: quem nunca escreveu não tem conversa para achar. Aqui o canal
 * é escolhido na hora e a conversa é aberta (ou reaproveitada) antes de a tela
 * trocar — então o campo de escrever já chega com a pessoa certa do outro lado.
 *
 * **Só canal CONECTADO aparece.** Oferecer um canal sem sessão daria uma
 * conversa que não envia, e a pessoa só descobriria depois de escrever a
 * mensagem inteira.
 */
export function EnviarMensagem({ contatoId, nome, tamanho = 'sm', variante = 'secondary' }: EnviarMensagemProps) {
    const navigate = useNavigate();
    const aviso = useAviso();

    const [aberto, setAberto] = useState(false);
    const [canais, setCanais] = useState<CanalDeIntegracao[] | null>(null);
    const [abrindo, setAbrindo] = useState<string | null>(null);
    const [erroDosCanais, setErroDosCanais] = useState<string | null>(null);

    /* Os canais são lidos ao abrir, e não na carga da tela: o estado de conexão
       muda por fora — alguém desconecta o WhatsApp no celular — e uma lista
       trazida junto com o cadastro estaria velha na hora de escolher. */
    useEffect(() => {
        if (!aberto) return;
        let vivo = true;
        setCanais(null);
        setErroDosCanais(null);
        dados().canaisDaClinica()
            .then((todos) => { if (vivo) setCanais(todos); })
            /* Erro ao ler NÃO é "nenhum canal conectado": a clínica ia reconectar
               um canal que estava no ar. */
            .catch((e: unknown) => { if (vivo) setErroDosCanais(e instanceof Error ? e.message : 'Tente de novo.'); });
        return () => { vivo = false; };
    }, [aberto]);

    const conectados = (canais ?? []).filter((c) => c.status === 'conectado');

    const escolher = async (canal: CanalDeIntegracao) => {
        setAbrindo(canal.id);
        try {
            const conversa = await dados().abrirConversaCom(contatoId, canal.id);
            setAberto(false);
            navigate(`/app/comunicacao?conversa=${conversa}`);
        } catch (e) {
            aviso({
                title: 'Não foi possível abrir a conversa',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setAbrindo(null);
        }
    };

    return (
        <>
            <Button
                size={tamanho} variant={variante}
                iconLeft={<Icon name="send" size={14} />}
                onClick={() => setAberto(true)}
            >
                Enviar mensagem
            </Button>

            <Modal
                open={aberto}
                title={`Enviar mensagem para ${nome}`}
                description="Escolha por onde falar. A conversa abre na Comunicação, pronta para escrever."
                onClose={() => setAberto(false)}
                width={480}
                footer={<Button variant="ghost" onClick={() => setAberto(false)}>Cancelar</Button>}
            >
                {erroDosCanais ? (
                    <Alert tone="danger" dismissible={false} title="Não foi possível ler os canais">{erroDosCanais}</Alert>
                ) : canais === null ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Vendo quais canais estão no ar…</p>
                ) : conectados.length === 0 ? (
                    <Alert tone="warning" dismissible={false} title="Nenhum canal conectado">
                        Só aparece aqui o canal com sessão no ar — um canal sem sessão daria uma conversa que não
                        envia. Conecte um em{' '}
                        <Link to="/app/configuracoes/integracoes" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                            Configurações › Integrações
                        </Link>
                        .
                    </Alert>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {conectados.map((canal) => (
                            <button
                                key={canal.id}
                                type="button"
                                disabled={abrindo !== null}
                                onClick={() => escolher(canal)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                                    padding: '12px 14px', textAlign: 'left', cursor: abrindo ? 'progress' : 'pointer',
                                    border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                                    background: 'var(--surface)', font: 'inherit', color: 'var(--text-primary)',
                                    transition: 'var(--transition-control)',
                                }}
                            >
                                <span
                                    aria-hidden
                                    style={{
                                        width: 34, height: 34, flex: '0 0 auto', borderRadius: 'var(--radius-sm)',
                                        background: 'var(--surface-secondary)', color: 'var(--neutral-700)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <Icon name={ICONE[canal.tipo] ?? 'message-circle'} size={16} />
                                </span>

                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <strong style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{canal.nome}</strong>
                                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                                        {canal.numero ?? canal.instanciaExternaId ?? canal.tipo}
                                    </span>
                                </span>

                                <Badge tone="success" dot>{abrindo === canal.id ? 'Abrindo…' : 'No ar'}</Badge>
                            </button>
                        ))}
                    </div>
                )}
            </Modal>
        </>
    );
}
